import { z } from 'zod';

import { ContractValidationError } from '@/domain/errors/contract-validation-error.js';
import { DEVELOPMENT_CATEGORIES, DEVELOPMENT_STATUSES } from '@/shared/utils/format-status.js';

const COMPLEXITY_LEVELS = ['MUITO_BAIXA', 'BAIXA', 'MEDIA', 'ALTA', 'MUITO_ALTA'] as const;

const DEV_DOCUMENT_TYPES = [
    'ESPECIFICACAO_FUNCIONAL',
    'DOCUMENTACAO_TECNICA',
    'EVIDENCIA_TESTES_DEV',
    'DOCUMENTO_CUTOVER',
    'EVIDENCIA_TESTES_CF',
    'EVIDENCIA_TESTES_UC',
    'ANALISE_IA'
] as const;

const SOLICITATION_TYPES = ['BUG', 'AJUSTE_MINOR'] as const;
const SOLICITATION_STATUSES = ['ABERTA', 'APROVADA', 'REJEITADA', 'RESOLVIDA'] as const;

const DevelopmentStatusSchema = z.enum(DEVELOPMENT_STATUSES);
const ComplexityLevelSchema = z.enum(COMPLEXITY_LEVELS);
const DevelopmentCategorySchema = z.enum(DEVELOPMENT_CATEGORIES);

const DeveloperAssignmentSchema = z
    .object({
        userId: z.string(),
        userName: z.string(),
        isLead: z.boolean()
    })
    .strict();

const DevelopmentDocumentSchema = z
    .object({
        id: z.string(),
        developmentId: z.string(),
        docType: z.enum(DEV_DOCUMENT_TYPES),
        fileName: z.string(),
        fileUrl: z.string(),
        fileSizeBytes: z.string().nullable(),
        mimeType: z.string().nullable(),
        version: z.number(),
        isCurrent: z.boolean(),
        uploadedAt: z.string(),
        uploadedBy: z.string().nullable(),
        uploadedByName: z.string().nullable()
    })
    .strict();

const DevelopmentEstimateSchema = z
    .object({
        id: z.string(),
        developmentId: z.string(),
        version: z.number(),
        isFromAi: z.boolean(),
        complexity: ComplexityLevelSchema.nullable(),
        estimatedHours: z.number().nullable(),
        estimatedDeliveryDate: z.string().nullable(),
        notes: z.string().nullable(),
        effortIncreaseReasonId: z.string().nullable(),
        effortIncreaseNotes: z.string().nullable(),
        rejected: z.boolean(),
        rejectionReasonId: z.string().nullable(),
        rejectionNotes: z.string().nullable(),
        rejectedBy: z.string().nullable(),
        rejectedAt: z.string().nullable(),
        createdAt: z.string(),
        createdBy: z.string().nullable()
    })
    .strict();

const SuggestedFixSchema = z
    .object({
        description: z.string(),
        severity: z.string(),
        reference: z.string(),
        pageRef: z.string().optional()
    })
    .strict();

const CriteriaScoreSchema = z
    .object({
        code: z.string(),
        name: z.string(),
        weight: z.number(),
        score: z.number(),
        maxScore: z.number()
    })
    .strict();

const EstimateEntrySchema = z
    .object({
        functionalComplexity: z.number(),
        developmentComplexity: z.number(),
        configSpecDoc: z.number(),
        testeFuncional: z.number(),
        desenvolvimento: z.number(),
        testeTecnico: z.number(),
        esforcoFuncional: z.number(),
        esforcoDesenvolvimento: z.number(),
        esforcoTotal: z.number()
    })
    .strict();

const UnderstandingEntrySchema = z
    .object({
        inOneSentence: z.string(),
        businessNeed: z.string(),
        whatWillBeDelivered: z.string(),
        howItWorks: z.string(),
        outOfScope: z.string(),
        confirmationChecklist: z.array(z.string())
    })
    .strict();

const TestDataEntrySchema = z
    .object({
        scenario: z.string(),
        responsible: z.string(),
        note: z.string()
    })
    .strict();

const PhaseResultEntrySchema = z
    .object({
        key: z.string(),
        summary: z.string(),
        findings: z.array(z.unknown()).optional(),
        blockingQuestions: z.array(z.string()).optional(),
        assumptions: z.array(z.string()).optional()
    })
    .strict();

const AiAnalysisSchema = z
    .object({
        id: z.string(),
        developmentId: z.string(),
        attemptNumber: z.number(),
        score: z.number(),
        minScoreUsed: z.number(),
        passed: z.boolean(),
        analysisSummary: z.string().nullable(),
        suggestedFixes: z.array(SuggestedFixSchema).nullable(),
        suggestedPbis: z.unknown().nullable(),
        suggestedComplexity: ComplexityLevelSchema.nullable(),
        suggestedHours: z.number().nullable(),
        suggestedArchitecture: z.string().nullable(),
        reportFileUrl: z.string().nullable(),
        rawResponse: z.string().nullable(),
        blockingQuestions: z.array(z.string()).nullable(),
        assumptions: z.array(z.string()).nullable(),
        criteriaScores: z.array(CriteriaScoreSchema).nullable(),
        strictnessLevelUsed: z.number().nullable(),
        modelUsed: z.string().nullable(),
        providerUsed: z.string().nullable(),
        extraPromptHash: z.string().nullable(),
        efInputMode: z.string().nullable(),
        analyzedAt: z.string(),
        analyzedBy: z.string().nullable(),
        wricefType: z.string().nullable().optional(),
        estimate: EstimateEntrySchema.nullable().optional(),
        understanding: UnderstandingEntrySchema.nullable().optional(),
        testDataClassification: z.array(TestDataEntrySchema).nullable().optional(),
        phaseResults: z.array(PhaseResultEntrySchema).nullable().optional(),
        analysisStatus: z.string().optional(),
        costUsd: z.number().nullable().optional(),
        sapValidationMode: z.enum(['live', 'degraded']).nullable().optional(),
        sapValidationSystem: z.string().nullable().optional(),
        sapValidationError: z.string().nullable().optional()
    })
    .strict();

const SolicitationSchema = z
    .object({
        id: z.string(),
        developmentId: z.string(),
        type: z.enum(SOLICITATION_TYPES),
        status: z.enum(SOLICITATION_STATUSES),
        title: z.string(),
        description: z.string(),
        steps: z.string().nullable(),
        ltResponse: z.string().nullable(),
        jiraCardKey: z.string().nullable(),
        jiraCardUrl: z.string().nullable(),
        openedBy: z.string().nullable(),
        evaluatedBy: z.string().nullable(),
        reclassifiedType: z.enum(SOLICITATION_TYPES).nullable(),
        rejectionReasonId: z.string().nullable(),
        rejectionNotes: z.string().nullable(),
        evaluatedAt: z.string().nullable(),
        expectedFixDate: z.string().nullable(),
        previousValidationStatus: DevelopmentStatusSchema.nullable(),
        jiraSync: z
            .object({
                status: z.enum(['NONE', 'PENDING', 'RETRYING', 'LINKED', 'FAILED']),
                nextAttemptAt: z.string().nullable()
            })
            .optional(),
        createdAt: z.string(),
        updatedAt: z.string()
    })
    .strict();

const DevelopmentBaseSchema = z.object({
    id: z.string(),
    displayId: z.string(),
    projectId: z.string(),
    businessProcessId: z.string().nullable(),
    title: z.string(),
    description: z.string().nullable(),
    status: DevelopmentStatusSchema,
    consultorId: z.string().nullable(),
    usuarioChaveId: z.string().nullable(),
    liderTecnicoId: z.string().nullable(),
    linkedDevelopmentId: z.string().nullable(),
    lastActiveStatus: DevelopmentStatusSchema.nullable(),
    cancelReason: z.string().nullable(),
    cancelReasonId: z.string().nullable(),
    cancelledBy: z.string().nullable(),
    cancelledAt: z.string().nullable(),
    efReturnCount: z.number(),
    functionalComplexity: ComplexityLevelSchema.nullable(),
    functionalHours: z.number().nullable(),
    techComplexity: ComplexityLevelSchema.nullable(),
    techHours: z.number().nullable(),
    techNotes: z.string().nullable(),
    category: DevelopmentCategorySchema.nullable(),
    cutoverNotRequired: z.boolean(),
    estimatedDeliveryDate: z.string().nullable(),
    startDate: z.string().nullable(),
    devEndDate: z.string().nullable(),
    validationEndDate: z.string().nullable(),
    closedAt: z.string().nullable(),
    closedBy: z.string().nullable(),
    validationReleasedAt: z.string().nullable(),
    jiraEpicKey: z.string().nullable(),
    jiraEpicUrl: z.string().nullable(),
    jiraEpicStatusName: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string()
});

export const DevelopmentSummarySchema = DevelopmentBaseSchema.extend({
    businessProcessName: z.string().nullable(),
    projectName: z.string(),
    projectAbbreviation: z.string(),
    clientName: z.string(),
    clientAbbreviation: z.string(),
    consultorName: z.string().nullable(),
    usuarioChaveName: z.string().nullable(),
    liderTecnicoName: z.string().nullable(),
    efValidatedAt: z.string().nullable(),
    jiraEpicSync: z.object({
        status: z.enum(['NONE', 'IDLE', 'PENDING', 'RETRYING', 'LINKED', 'FAILED']),
        nextAttemptAt: z.string().nullable()
    })
}).strict();

export const DevelopmentDetailSchema = DevelopmentSummarySchema.extend({
    developers: z.array(DeveloperAssignmentSchema),
    documents: z.array(DevelopmentDocumentSchema),
    estimates: z.array(DevelopmentEstimateSchema),
    aiAnalyses: z.array(AiAnalysisSchema),
    solicitations: z.array(SolicitationSchema),
    efValidatedAt: z.string().nullable().optional(),
    jiraLastError: z.string().nullable(),
    jiraEpicCreateOpen: z.boolean(),
    jiraStoriesSync: z.object({
        status: z.enum(['NONE', 'GENERATING', 'CREATING', 'RETRYING', 'DONE', 'FAILED']),
        created: z.number(),
        total: z.number(),
        nextAttemptAt: z.string().nullable()
    })
}).strict();

export const DevelopmentListResponseSchema = z
    .object({
        data: z.array(DevelopmentSummarySchema),
        total: z.number(),
        page: z.number(),
        limit: z.number(),
        totalPages: z.number()
    })
    .strict();

export type DevelopmentSummaryContract = z.infer<typeof DevelopmentSummarySchema>;
export type DevelopmentDetailContract = z.infer<typeof DevelopmentDetailSchema>;
export type DevelopmentListResponseContract = z.infer<typeof DevelopmentListResponseSchema>;

function parse<T>(schema: z.ZodType<T>, payload: unknown, contractName: string): T {
    const result = schema.safeParse(payload);
    if (!result.success) {
        const issues = result.error.issues.map((issue) => ({ path: issue.path, message: issue.message }));
        const summary = issues.map((issue) => `${issue.path.join('.') || '(root)'}: ${issue.message}`).join('; ');
        throw new ContractValidationError(`${contractName} contract violation: ${summary}`, issues);
    }
    return result.data;
}

export function parseDevelopmentSummaryList(payload: unknown): DevelopmentListResponseContract {
    return parse(DevelopmentListResponseSchema, payload, 'DevelopmentListResponse');
}

export function parseDevelopmentDetail(payload: unknown): DevelopmentDetailContract {
    return parse(DevelopmentDetailSchema, payload, 'DevelopmentDetail');
}
